import React, { useState, useCallback } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@mui/styles';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import ControlCameraIcon from '@mui/icons-material/ControlCamera';

import Moment from 'moment';

import Tooltip from '../../components/styled/tooltip';

import HealthAlerts from './widgets/cmc/health-alerts';
import AlertsSeverity from './widgets/cmc/alerts-severity';
import AlertsGroup from './widgets/cmc/alerts-group';
import AlertsPoliciesWidget from './widgets/cmc/alerts-policy';
import AlertsActiveTags from './widgets/cmc/alerts-active-tags';
import AlertsTimelineWidget from './widgets/cmc/alerts-timeline';
import MtdtIPActivity from './widgets/cmc/mtdt-ip-activity';
import MtdtDNSActivity from './widgets/cmc/mtdt-dns-activity';
import MtdtActiveTags from './widgets/cmc/mtdt-active-tags';
import MtdtTimeline from './widgets/cmc/mtdt-timeline';
import MtdtGroup from './widgets/cmc/mtdt-group';
import UserAgentsWidget from './widgets/cmc/user-agents';
import HealthMetric from './widgets/cmc/health-metric';

import MC2AlertsCounts from './widgets/mc2/alerts-count';
import MC2AlertsSeverity from './widgets/mc2/alerts-severity';
import MC2MtdtCounts from './widgets/mc2/mtdt-count';
import MC2MtdtTimeline from './widgets/mc2/mtdt-timeline';
import MC2HealthAlerts from './widgets/mc2/health-alerts';
import MC2HealthMetric from './widgets/mc2/health-metric';

import MetadataService from '../../data/metadata-service';

const useStyles = makeStyles(theme => ({
    root: {
        padding: theme.spacing(2),
        height: '100%',
    },
    edit: {
        backgroundColor: theme.styled.paper.background,
        border: 'solid',
        borderColor: theme.styled.paper.border,
        borderWidth: 2,
        // overflow: 'scroll',
    },
    view: {
        backgroundColor: theme.palette.background.paper,
    },
    header: {
        ...theme.typography.subtitle1,
        alignSelf: 'flex-start',
        width: '100%',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
    body: {
        alignSelf: 'center',
        justifyContent: 'center',
        width: '100%',
        // overflow: 'auto',
    },
    footer: {
        ...theme.typography.caption,
        alignSelf: 'flex-end',
        width: '100%',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
    primary: {
        color: theme.palette.primary.main,
    },
    error: {
        color: theme.palette.error.main,
    },
    drag: {
        cursor: 'move',
        height: 20,
    },
    elipsis: {
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
    },
}));

const Widget = React.forwardRef((props, ref) => {
    const {
        data,
        className,
        children,
        onEdit,
        onFinishEdit,
        onUpdateWidget, // from widget view itself (e.g. remove user agents)
        onDelete,
        width,
        height,
        ...rest
    } = props;
    const classes = useStyles();
    const theme = useTheme();

    const [hover, setHover] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(false);
    const [asof, setAsof] = useState(Moment());

    const handleEdit = e => {
        // refresh sources cache so we have latest sources in popup.
        MetadataService.cacheFields().catch(err => console.log(err));
        setHover(false);
        setBusy(false);
        setError(false);
        onEdit(data);
    };

    const handleCancelEdit = () => {
        setHover(false);
        onFinishEdit({ ...data, edit: false });
    };

    const handleFinishEdit = d => {
        setHover(false);
        onFinishEdit(d);
    };

    const handleDelete = d => {
        onDelete(data);
    };

    const handleSetBusy = useCallback((b, error) => {
        setBusy(b);
        if (!b) setAsof(Moment());
        setError(error);
    }, []);

    const handleOnError = useCallback((err, title) => {
        console.log(title, err);
    }, []);

    const wprops = {
        ...{ data, hover },
        size: {
            height: height - 4 * theme.getSpacing(2),
            width: width - 2 * theme.getSpacing(2),
        },
        onError: handleOnError,
        setBusy: handleSetBusy,
        onOK: handleFinishEdit,
        onCancel: handleCancelEdit,
        onUpdate: onUpdateWidget,
    };

    return (
        <Box ref={ref} { ...rest } style={{ height: '100%' }}>
            <Box
                { ...rest }
                display="flex"
                flexWrap="wrap"
                className={clsx(classes.root, {
                    [classes.edit]: data.edit,
                    [classes.view]: !data.edit,
                }, className)}
                onMouseEnter={() => setHover(!data.edit && true)}
                onMouseLeave={() => setHover()}>
                <Box className={clsx(classes.header)} style={{ position: 'relative' }}>
                    <Grid container
                        direction="row"
                        wrap='nowrap'
                        justifyContent="space-between">
                        <Grid item xs={7} className={classes.elipsis}>
                            {data.title}
                            <Typography
                                component="span"
                                variant="caption"
                                className={classes.primary}
                                style={{ marginLeft: 25 }}>
                            </Typography>
                        </Grid>
                        <div className={clsx({ 'drag-handle': !data.edit && hover }, classes.primary)}
                            style={{ height: 20, position: 'absolute', left: '50%' }}>
                            {hover && <ControlCameraIcon
                                className={classes.drag}
                                fontSize='small'/>}
                        </div>
                        <Grid item>
                            {!data.static && hover && !data.edit &&
                                <IconButton
                                    title="configure"
                                    onClick={handleEdit}
                                    aria-label={`configure ${data.i} widget`}
                                    style={{ padding: 0 }}
                                    size="large">
                                    <SettingsIcon/>
                                </IconButton>}
                            {!data.static && data.edit &&
                                <IconButton
                                    title="save"
                                    onClick={handleCancelEdit}
                                    aria-label="save changes"
                                    style={{ padding: 0 }}
                                    size="large">
                                    <CloseIcon/>
                                </IconButton>}
                            {!data.static && hover && !data.edit &&
                                <IconButton
                                    title="delete"
                                    onClick={handleDelete}
                                    color="secondary"
                                    aria-label="delete widget"
                                    style={{ padding: 0 }}
                                    size="large">
                                    <DeleteIcon/>
                                </IconButton>}
                        </Grid>
                    </Grid>
                </Box>

                <Box className={classes.body}>
                    {{
                        'brc-widget-alert-severity': <AlertsSeverity {...wprops } />,
                        'brc-widget-alert-group': <AlertsGroup {...wprops } />,
                        'brc-widget-alert-policy': <AlertsPoliciesWidget {...wprops } />,
                        'brc-widget-active-alert-tags': <AlertsActiveTags {...wprops } />,
                        'brc-widget-alert-timeline': <AlertsTimelineWidget {...wprops } />,
                        'brc-widget-active-metadata-tags': <MtdtActiveTags {...wprops } />,
                        'brc-widget-dns-activity': <MtdtDNSActivity {...wprops } />,
                        'brc-widget-mtdt-timeline': <MtdtTimeline {...wprops } />,
                        'brc-widget-mtdt-group': <MtdtGroup {...wprops } />,
                        'brc-widget-mtdt-ip-activity': <MtdtIPActivity {...wprops } />,
                        'brc-widget-health-alerts': <HealthAlerts {...wprops } />,
                        'brc-widget-user-agents': <UserAgentsWidget {...wprops } />,
                        'brc-widget-health-metric': <HealthMetric {...wprops } />,

                        'brc-widget-mc2-health-alerts': <MC2HealthAlerts {...wprops } />,
                        'brc-widget-mc2-health-metric': <MC2HealthMetric {...wprops } />,
                        'brc-widget-mc2-alert-severity': <MC2AlertsSeverity {...wprops } />,
                        'brc-widget-mc2-alert-counts': <MC2AlertsCounts {...wprops } />,
                        'brc-widget-mc2-mtdt-counts': <MC2MtdtCounts {...wprops } />,
                        'brc-widget-mc2-mtdt-timeline': <MC2MtdtTimeline {...wprops } />,
                    }[data.type.key]}
                </Box>

                <Box className={classes.footer}>
                    <Grid container
                        direction="row"
                        wrap='nowrap'
                        justifyContent="space-between"
                        className={classes.primary}>
                        <Grid item>
                            {hover && `Data as of ${asof.format(theme.date.long)}`}
                        </Grid>
                        <Grid item>
                            {busy && <CircularProgress size={20}/>}
                        </Grid>
                        {!busy && error &&
                            <Grid item>
                                <Tooltip
                                    placement="top"
                                    title="Widget is paused due to errors. Please check configuration.">
                                    <WarningIcon fontSize='small' className={classes.error}/>
                                </Tooltip>
                            </Grid>
                        }
                    </Grid>
                    &nbsp;
                </Box>
                {children}
            </Box>
        </Box>
    );
});

Widget.propTypes = {
    data: PropTypes.object.isRequired,
    className: PropTypes.string,
    children: PropTypes.node,
    onEdit: PropTypes.func.isRequired,
    onFinishEdit: PropTypes.func.isRequired,
    onUpdateWidget: PropTypes.func.isRequired,
    onDelete: PropTypes.func,
    width: PropTypes.number,
    height: PropTypes.number,
};
Widget.displayName = 'Widget';

const Wrapper = React.forwardRef(({ style, children, ...rest }, ref) => {
    const newChildren = React.Children.map(children, function (child) {
        return React.cloneElement(child, {
            width: +(style.width || '').split('px').slice(0, -1),
            height: +(style.height || '').split('px').slice(0, -1),
        });
    });
    return (
        <div {...rest} style={style} ref={ref}>
            {newChildren}
        </div>
    );
});
Wrapper.propTypes = {
    children: PropTypes.node,
    style: PropTypes.object,
};
Wrapper.displayName = 'Wrapper';

const VerifyDates = ({ start, end, quick, override }) => {
    if (!override) return true;
    if (!quick && !Moment(start).isValid()) return false;
    if (!quick && end && !Moment(end).isValid()) return false;
    if (!quick && end && Moment(start).isAfter(Moment(end))) return false;
    return true;
};

export {
    Widget as default,
    Wrapper,
    VerifyDates,
};
